import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../../config/api';
import './../../../styles/TermSheetViewer.css';
import CompanyLogo from '../../common/CompanyLogo';

const TermSheetViewer: React.FC = () => {
  const { selectionId } = useParams<{ selectionId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [selectionData, setSelectionData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Debug logging
  console.log('=== TERM SHEET VIEWER RENDER ===');
  console.log('selectionId from URL params:', selectionId);
  console.log('Current URL:', window.location.href);
  
  // Check authentication status
  const token = localStorage.getItem('token');
  console.log('Authentication check - Token exists:', !!token);
  if (token) {
    console.log('Token preview:', token.substring(0, 20) + '...');
  }

  const fetchTermSheetData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check authentication
      const token = localStorage.getItem('token');
      const isAuthenticated = !!token;
      
      // Validate selectionId
      if (!selectionId) {
        setError('No selectionId available. Please ensure you have completed the deal options step.');
        return;
      }
      
      console.log('Fetching selection data for selectionId:', selectionId);
      
      // Get selection data (if authenticated)
      let facilityId = null;
      
      if (isAuthenticated) {
        try {
          const selectionResponse = await axios.get(
            `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/user-selections/${selectionId}`
          );
          
          setSelectionData(selectionResponse.data.data);
          
          // Get facilityId from selection data
          facilityId = selectionResponse.data.data?.facilityId;
          console.log('FacilityId from selection data:', facilityId);
        } catch (selectionError) {
          console.log('Could not fetch selection data:', selectionError);
        }
      }
      
      if (!facilityId) {
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          facilityId = user.facilityId;
          console.log('FacilityId from user data:', facilityId);
        }
      }
      
      // If still no facilityId, try to get it from the user's current session
      if (!facilityId) {
        try {
          // Try to get user data from localStorage first
          const userData = localStorage.getItem('user');
          if (userData) {
            const user = JSON.parse(userData);
            facilityId = user.facilityId;
            console.log('FacilityId from localStorage user data:', facilityId);
          }
        } catch (profileError) {
          console.log('Could not get facilityId from localStorage:', profileError);
        }
      }
      
      // For authenticated users, we need a facilityId
      if (isAuthenticated && !facilityId) {
        setError('No facility ID available. Please ensure you have completed the deal options step.');
        return;
      }
      
      // Validate that facilityId is a valid MongoDB ObjectId (24 hex characters) for authenticated users
      if (isAuthenticated && facilityId) {
        const objectIdRegex = /^[0-9a-fA-F]{24}$/;
        if (!objectIdRegex.test(facilityId)) {
          console.error('Invalid facilityId format:', facilityId);
          setError('Invalid facility ID format. Please ensure you have completed the deal options step.');
          return;
        }
      }
      
      // Set embed URL based on authentication status
      if (isAuthenticated) {
        // Use authenticated term sheet endpoint
        setEmbedUrl(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/document/term-sheet/${facilityId}?format=docx&inline=true`);
      } else {
        // Use demo endpoint with selectionId
        setEmbedUrl(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/document/download/demo/${selectionId}?format=docx&inline=true`);
      }
      
    } catch (error: any) {
      console.error('Error fetching term sheet:', error);
      
      if (error.response?.status === 404) {
        setError('Selection not found. Please ensure you have completed the deal options step.');
      } else if (error.response?.status === 401) {
        setError('Authentication required. Please log in and try again.');
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to load term sheet. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectionId) {
      fetchTermSheetData();
    }
  }, [selectionId]);

  const downloadTermSheet = async (format: 'docx' = 'docx') => {
    try {
      console.log('=== TERM SHEET DOWNLOAD START ===');
      console.log('Downloading term sheet:', { selectionId, format });
      
      // Check if user is logged in and has a token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please log in and try again.');
      }
      
      console.log('Token found:', token.substring(0, 20) + '...');
      
      // Validate selectionId
      if (!selectionId) {
        throw new Error('No selectionId available. Please ensure you have completed the deal options step.');
      }
      
      // Get facilityId from selectionData or user data
      let facilityId = selectionData?.facilityId;
      console.log('Download - FacilityId from selectionData:', facilityId);
      
      if (!facilityId) {
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          facilityId = user.facilityId;
          console.log('Download - FacilityId from user data:', facilityId);
        }
      }
      
      // If still no facilityId, try to get it from the user's current session
      if (!facilityId) {
        try {
          // Try to get user data from localStorage first
          const userData = localStorage.getItem('user');
          if (userData) {
            const user = JSON.parse(userData);
            facilityId = user.facilityId;
            console.log('Download - FacilityId from localStorage user data:', facilityId);
          }
        } catch (profileError) {
          console.log('Download - Could not get facilityId from localStorage:', profileError);
        }
      }
      
      if (!facilityId) {
        throw new Error('No facility ID available. Please ensure you have completed the deal options step.');
      }
      
      // Validate that facilityId is a valid MongoDB ObjectId (24 hex characters)
      const objectIdRegex = /^[0-9a-fA-F]{24}$/;
      if (!objectIdRegex.test(facilityId)) {
        console.error('Invalid facilityId format:', facilityId);
        throw new Error('Invalid facility ID format. Please ensure you have completed the deal options step.');
      }
      
      console.log('Using new authenticated endpoint with facilityId:', facilityId);
      console.log('API URL:', `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/document/term-sheet/${facilityId}`);
      
      // Force DOCX format - no PDF to avoid API dependencies
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/document/term-sheet/${facilityId}`,
        {
          params: { format: 'docx' }, // Always DOCX - simple template merge only
          responseType: 'blob',
          timeout: 30000, // Reduced timeout for simple operation
          headers: {
            'Authorization': `Bearer ${token}` // Explicitly add token to ensure it's sent
          }
        }
      );
      
      console.log('Download response received:', {
        status: response.status,
        contentType: response.headers['content-type'],
        contentDisposition: response.headers['content-disposition'],
        size: response.data.length,
        dataType: typeof response.data
      });
      
      if (!response.data || response.data.length === 0) {
        throw new Error('Received empty response from server');
      }
      
      console.log('Creating blob URL...');
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/octet-stream' 
      });
      const url = window.URL.createObjectURL(blob);
      
      console.log('Creating download link...');
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TermSheet-${selectionData?.ticker || 'document'}.docx`);
      link.style.display = 'none';
      
      console.log('Triggering download...');
      document.body.appendChild(link);
      link.click();
      
      console.log('Cleaning up...');
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      console.log('✅ Term sheet downloaded successfully');
    } catch (error: any) {
      console.error('=== TERM SHEET DOWNLOAD ERROR ===');
      console.error('Download failed:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      });
      
      if (error.message?.includes('No authentication token found')) {
        alert('Please log in to download the term sheet.');
        navigate('/login');
      } else if (error.response?.status === 401) {
        alert('Authentication required. Please log in and try again.');
        navigate('/login');
      } else if (error.response?.status === 403) {
        alert('You do not have permission to access this term sheet.');
      } else if (error.response?.status === 404) {
        alert('Facility not found. Please ensure you have completed the deal options step and try again.');
      } else if (error.response?.status === 500) {
        alert('Server error occurred while generating the term sheet. Please try again later.');
      } else if (error.response?.data?.message) {
        alert(`Download failed: ${error.response.data.message}`);
      } else if (error.code === 'ECONNABORTED') {
        alert('Download timed out. Please try again.');
      } else {
        alert(`Failed to download term sheet: ${error.message}`);
      }
    }
  };

  const handleBackToTerms = () => {
    navigate(`/deal-options/${selectionId}`);
  };

  const handleContinue = () => {
    navigate(`/questionnaire/${selectionId}`);
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <h1>Term Sheet Review</h1>
        {selectionData && (
          <div className="flex items-center justify-center gap-4 mt-4">
            <CompanyLogo 
              ticker={selectionData.ticker} 
              companyName={selectionData.companyName} 
              size="lg" 
            />
            <p className="subtitle">
              {selectionData.companyName} ({selectionData.ticker}) - 
              ${(selectionData.selectedAmount || 0).toLocaleString()} Facility
            </p>
          </div>
        )}
      </div>

      <div className="pdf-viewer-container">
        <div className="pdf-toolbar">
          <button 
            className="btn btn-secondary" 
            onClick={handleBackToTerms}
            disabled={loading}
          >
            ← Back to Terms
          </button>
          <div className="toolbar-actions">
            <button 
              className="btn btn-primary" 
              onClick={() => downloadTermSheet('docx')}
              disabled={loading}
            >
              Download Term Sheet
            </button>
          </div>
        </div>

        <div className="pdf-viewer">
          {loading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading term sheet...</p>
            </div>
          ) : embedUrl ? (
            <iframe
              src={embedUrl}
              className="w-full"
              style={{ height: '80vh', border: 'none' }}
              title="Term Sheet Viewer"
            />
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">{error}</p>
              <div className="space-x-4">
                <button
                  onClick={() => fetchTermSheetData()}
                  className="btn btn-primary"
                >
                  Try Again
                </button>
                <button
                  onClick={() => downloadTermSheet('docx')}
                  className="btn btn-secondary"
                >
                  Download Instead
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="pdf-viewer-footer">
          <p className="info-text">
            <i className="fas fa-info-circle"></i>
            This term sheet outlines the key terms of your equity facility. 
            Please review carefully before proceeding.
          </p>
          <button 
            className="btn btn-primary btn-large"
            onClick={handleContinue}
            disabled={loading}
          >
            Continue to Account Setup →
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermSheetViewer; 